import React, { useMemo } from "react";
import { ReactFlow, Background, Controls, MarkerType } from "@xyflow/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowUp, ArrowDown, Trash2, Plus } from "lucide-react";

const NODE_TYPES = [
  { value: "start", label: "Start" },
  { value: "process", label: "Process" },
  { value: "decision", label: "Decision" },
  { value: "end", label: "End" },
];

const nodeStyle = (type) => {
  const base = {
    fontSize: 12,
    fontWeight: 500,
    padding: "8px 14px",
    border: "1px solid",
    width: 190,
    textAlign: "center",
  };
  if (type === "start" || type === "end")
    return { ...base, background: "#002FA7", color: "#fff", borderColor: "#002FA7", borderRadius: 999 };
  if (type === "decision")
    return { ...base, background: "#FFFBEB", color: "#78350F", borderColor: "#FBBF24", borderRadius: 4 };
  return { ...base, background: "#fff", color: "#1E293B", borderColor: "#CBD5E1", borderRadius: 4 };
};

const FlowchartEditor = ({ flowchart, setFlowchart }) => {
  const nodes = useMemo(
    () =>
      flowchart.map((n, i) => ({
        id: n.id,
        data: { label: n.label },
        position: { x: 40, y: i * 90 },
        style: nodeStyle(n.type),
        sourcePosition: "bottom",
        targetPosition: "top",
      })),
    [flowchart]
  );

  const edges = useMemo(
    () =>
      flowchart.slice(0, -1).map((n, i) => ({
        id: `e-${n.id}-${flowchart[i + 1].id}`,
        source: n.id,
        target: flowchart[i + 1].id,
        animated: true,
        style: { stroke: "#002FA7", strokeWidth: 1.5 },
        markerEnd: { type: MarkerType.ArrowClosed, color: "#002FA7" },
      })),
    [flowchart]
  );

  const update = (idx, patch) => {
    const next = flowchart.map((n, i) => (i === idx ? { ...n, ...patch } : n));
    setFlowchart(next);
  };
  const remove = (idx) => setFlowchart(flowchart.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= flowchart.length) return;
    const next = [...flowchart];
    [next[idx], next[j]] = [next[j], next[idx]];
    setFlowchart(next);
  };
  const add = () => {
    const id = `n${Date.now()}`;
    setFlowchart([...flowchart, { id, label: "New step", type: "process" }]);
  };

  return (
    <div className="space-y-4" data-testid="flowchart-editor">
      <div className="h-[260px] border border-slate-200 bg-slate-50 rounded-md overflow-hidden">
        {flowchart.length > 0 ? (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            fitView
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#cbd5e1" gap={16} />
            <Controls showInteractive={false} />
          </ReactFlow>
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-slate-400 px-6 text-center">
            No flowchart yet. Generate a document or add steps manually below.
          </div>
        )}
      </div>

      <div className="space-y-2">
        {flowchart.map((n, i) => (
          <div key={n.id} className="flex items-center gap-2 bg-white border border-slate-200 rounded-md p-2" data-testid={`flow-step-${i}`}>
            <div className="flex flex-col gap-0.5">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-slate-800 disabled:opacity-30" data-testid={`flow-up-${i}`}>
                <ArrowUp size={14} />
              </button>
              <button onClick={() => move(i, 1)} disabled={i === flowchart.length - 1} className="text-slate-400 hover:text-slate-800 disabled:opacity-30" data-testid={`flow-down-${i}`}>
                <ArrowDown size={14} />
              </button>
            </div>
            <Input
              value={n.label}
              onChange={(e) => update(i, { label: e.target.value })}
              className="h-8 text-sm flex-1"
              data-testid={`flow-label-${i}`}
            />
            <Select value={n.type} onValueChange={(v) => update(i, { type: v })}>
              <SelectTrigger className="h-8 w-[110px] text-xs" data-testid={`flow-type-${i}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NODE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value} className="text-xs">
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button onClick={() => remove(i)} className="text-slate-400 hover:text-red-600" data-testid={`flow-remove-${i}`}>
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>

      <Button variant="outline" onClick={add} className="w-full gap-2" data-testid="flow-add-step-btn">
        <Plus size={15} /> Add Step
      </Button>
    </div>
  );
};

export default FlowchartEditor;
